// src/containers/FormBuilderDashboard/index.jsx   (or wherever your main file is)
import React from 'react';
import { Box, Typography } from '@mui/material';
import FormList from './components/FormList';
import FormEditor from './components/FormEditor';
import PresetsPanel from './PresetsPanel';
import useFormEditor from './hooks/useFormEditor';
import NewFormModal from './components/NewFormModal';
import AddSectionModal from './components/AddSectionModal';

class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('FormBuilderDashboard error:', error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 3, color: 'red' }}>
          <Typography variant="h6">An error occurred</Typography>
          <Typography>{this.state.error.message}</Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

const FormBuilderDashboard = () => {
  const {
    custom_forms,
    editingForm,
    setEditingForm,
    formName,
    setFormName,
    sections,
    setSections,
    headers,
    setHeaders,
    formTotaling,
    setFormTotaling,
    sendSftp,           // ← NEW
    setSendSftp,        // ← NEW
    sectionCounter,
    setSectionCounter,
    error,
    setError,
    sectionModalOpen,
    setSectionModalOpen,
    newSectionName,
    setNewSectionName,
    newFormModalOpen,
    setNewFormModalOpen,
    newFormName,
    setNewFormName,
    saving,
    isDataReady,
    tableRefs,
    handleEdit,
    handleBack,
    handleSave,
    handleDelete,
    openNewFormModal,
    closeNewFormModal,
    handleCreateNewForm,
    openAddSectionModal,
    closeAddSectionModal,
    handleAddSection,
  } = useFormEditor();

  const sectionOrder = Object.values(sections) || [];
  const formKey = editingForm?.id || 'new';

  return (
    <ErrorBoundary>
      <Box sx={{ p: 3, display: 'flex', gap: 3, height: 'calc(100vh - 64px)' }}>
        <Box sx={{ width: 380, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormList
            custom_forms={custom_forms}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            openNewFormModal={openNewFormModal}
          />
          <PresetsPanel
            onAddPreset={(sectionId, newItem) => {
              if (!sectionId || !sections[sectionId]) return;
              if (isNaN(newItem.presetId)) return;
              setSections(prev => ({
                ...prev,
                [sectionId]: {
                  ...prev[sectionId],
                  items: [...(prev[sectionId]?.items || []), newItem],
                },
              }));
            }}
            sectionOrder={sectionOrder}
          />
        </Box>

        <FormEditor
          editingForm={editingForm}
          setEditingForm={setEditingForm}
          formName={formName}
          setFormName={setFormName}
          sections={sections}
          setSections={setSections}
          headers={headers}
          setHeaders={setHeaders}
          formTotaling={formTotaling}
          setFormTotaling={setFormTotaling}
          sendSftp={sendSftp}           // ← NEW - Passing down
          setSendSftp={setSendSftp}     // ← NEW - Passing down
          error={error}
          tableRefs={tableRefs}
          handleBack={handleBack}
          handleSave={handleSave}
          openAddSectionModal={openAddSectionModal}
          sectionModalOpen={sectionModalOpen}
          newSectionName={newSectionName}
          setNewSectionName={setNewSectionName}
          closeAddSectionModal={closeAddSectionModal}
          handleAddSection={handleAddSection}
          saving={saving}
          isDataReady={isDataReady}
          formKey={formKey}
        />
        
        <NewFormModal
          open={newFormModalOpen}
          newFormName={newFormName}
          setNewFormName={setNewFormName}
          onClose={closeNewFormModal}
          onCreate={handleCreateNewForm}
        />
        <AddSectionModal
          open={sectionModalOpen}
          newSectionName={newSectionName}
          setNewSectionName={setNewSectionName}
          onClose={closeAddSectionModal}
          onAdd={handleAddSection}
        />
      </Box>
    </ErrorBoundary>
  );
};

export default FormBuilderDashboard;